import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/firebase-client';
import { useAuth } from '@/hooks/useAuth';

export type EmployerAccessStatus = 'active' | 'pending' | 'needs_onboarding';

export interface EmployerAccessState {
  status: EmployerAccessStatus;
  companyId: string | null;
  companyName: string | null;
  requestId: string | null;
}

export function useEmployerAccessState(enabled = true) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['employer-access-state', user?.id],
    enabled: enabled && !!user?.id,
    staleTime: 1000 * 60,
    queryFn: async (): Promise<EmployerAccessState> => {
      if (!user?.id) {
        return { status: 'needs_onboarding', companyId: null, companyName: null, requestId: null };
      }

      const { data: ownedCompanies } = await supabase
        .from('companies')
        .select('id, name')
        .eq('owner_id', user.id)
        .limit(1);

      if (ownedCompanies?.length) {
        return {
          status: 'active',
          companyId: ownedCompanies[0].id,
          companyName: ownedCompanies[0].name || null,
          requestId: null,
        };
      }

      const { data: activeMemberships } = await supabase
        .from('organization_members')
        .select('company_id')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .limit(1);

      if (activeMemberships?.length) {
        const { data: company } = await supabase
          .from('companies')
          .select('id, name')
          .eq('id', activeMemberships[0].company_id)
          .maybeSingle();

        return {
          status: 'active',
          companyId: company?.id || activeMemberships[0].company_id,
          companyName: company?.name || null,
          requestId: null,
        };
      }

      const { data: pendingRequests } = await supabase
        .from('organization_join_requests')
        .select('id, company_id')
        .eq('user_id', user.id)
        .eq('status', 'pending')
        .order('requested_at', { ascending: false })
        .limit(1);

      if (pendingRequests?.length) {
        const { data: company } = await supabase
          .from('companies')
          .select('id, name')
          .eq('id', pendingRequests[0].company_id)
          .maybeSingle();

        return {
          status: 'pending',
          companyId: pendingRequests[0].company_id,
          companyName: company?.name || null,
          requestId: pendingRequests[0].id,
        };
      }

      return { status: 'needs_onboarding', companyId: null, companyName: null, requestId: null };
    },
  });
}
